// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";
import { push } from "connected-react-router";

import http from "@http";
import { route } from "@routes/helper";
import actionCreators from "@modules/service/actionCreator";
import type {
  StandardQuestionsResponse,
  StandardQuestion,
} from "@modules/service/reducer/standard-questions/types";
import { ApprovalStateEnum } from "@modules/service/constants";
import type {
  HierarchyAssignment,
  ApprovableContainer,
  HierarchyAssignmentSummary,
} from "@modules/service/types";
import {
  GetStandardQuestions,
  AddNewStandardQuestion,
  GetStandardQuestion,
  UpdateStandardQuestion,
  UpdateStandardQuestionAssignments,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { sortToString, transformSort } from "@transformers/sort";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type ItemResponse<T> = $Exact<T>;

type StandardQuestionsListRequest = $Exact<{
  clientId: string,
  isActive?: boolean,
  page: number,
  search: Object,
  sort: Object,
}>;

type AddStandardQuestionsRequest = $Exact<{
  clientId: string,
  draft: StandardQuestion,
  assignments: Array<HierarchyAssignment>,
  filter: string,
}>;

export type StandardQuestionRequest = $Exact<{
  ...StandardQuestionsListRequest,
  stableId: string,
}>;

export type StandardQuestionSubmitForReviewRequest = $Exact<{
  ...StandardQuestionRequest,
  notes: string,
}>;
type StandardQuestionUpdateRequest = $Exact<{
  ...StandardQuestionRequest,
  draft: StandardQuestion,
}>;

export function* onGetStandardQuestions({
  payload,
}: Action<StandardQuestionsListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<StandardQuestionsResponse>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question`,
      {
        params: {
          limit: 0,
          page: payload.page ? payload.page : 0,
          sort: payload.sort ? sortToString(transformSort(payload.sort)) : "",
          isActive: payload.isActive !== null ? !!payload.isActive : null,
        },
      },
    );

    yield put(actionCreators.GetStandardQuestionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionsError(e.response));
  }
}

export function* onAddNewStandardQuestion({
  payload,
}: Action<AddStandardQuestionsRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.draft || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.post,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question`,
      payload.draft,
    );

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${response.data.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield all([
      put(actionCreators.AddNewStandardQuestionSuccess(response.data)),
      put(
        actionCreators.GetStandardQuestions({
          clientId: payload.clientId,
          isActive: payload.filter !== "ALL" ? payload.filter == "ACTIVE" : null,
        }),
      ),
      put(actionCreators.SetShowAddStandardQuestionModal({ show: false, step: 1 })),
      put(sendSuccessMessage("STANDARD_QUESTION_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddNewStandardQuestionError(e.response));
  }
}

export function* onGetStandardQuestion({
  payload,
}: Action<StandardQuestionRequest>): Generator<*, void, { data: StandardQuestion }> {
  try {
    if (!payload || !payload.clientId || !payload.stableId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const stableResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}`,
    );
    const assignmentsResponse = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}/assignment`,
    );

    yield put(actionCreators.GetStandardQuestionSuccess(stableResponse.data));
    yield put(actionCreators.SetStandardQuestionAssignments(assignmentsResponse.data));
  } catch (e) {
    yield put(actionCreators.GetStandardQuestionError(e.response));
  }
}

export function* onUpdateStandardQuestion({
  payload,
}: Action<StandardQuestionUpdateRequest>): Generator<
  *,
  void,
  { data: ItemResponse<ApprovableContainer<StandardQuestion>> },
> {
  try {
    if (!payload || !payload.clientId || !payload.stableId || !payload.draft) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/standard-question/question/${payload.stableId}`,
      payload.draft,
    );

    yield all([
      put(actionCreators.UpdateStandardQuestionSuccess(response.data)),
      put(actionCreators.ShowStandardQuestionEditModal(false)),
      put(push(route("ADMINISTRATION_SERVICE_FEATURES_QUESTIONS", { clientId: payload.clientId }))),
      put(sendSuccessMessage("STANDARD_QUESTION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateStandardQuestionError(e.response));
  }
}

// REFRESH

type RefreshRequest = $Exact<{
  clientId: string,
  stableId?: string,
}>;

export function* onRefresh(params: RefreshRequest): Generator<*, void, Object> {
  try {
    yield put(actionCreators.GetStandardQuestions({ clientId: params.clientId }));
    if (params.stableId) {
      yield put(
        actionCreators.GetStandardQuestion({
          clientId: params.clientId,
          stableId: params.stableId,
        }),
      );
    }
  } catch (e) {
    throw e;
  }
}

type StandardQuestionUpdateAssignmentsRequest = $Exact<{
  container: ApprovableContainer<StandardQuestion>,
  assignments: Array<HierarchyAssignment>,
}>;

export function* onUpdateStandardQuestionAssignments({
  payload,
}: Action<StandardQuestionUpdateAssignmentsRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.container || !payload.assignments) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const { data: prevAssignments } = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment`,
    );
    if (prevAssignments && prevAssignments.length) {
      yield all([
        ...prevAssignments.map((a: HierarchyAssignmentSummary) => {
          return call(
            http.delete,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment/${a.id}`,
          );
        }),
      ]);
    }

    if (payload.assignments.length) {
      yield all([
        ...payload.assignments.map((a: HierarchyAssignment) => {
          return call(
            http.post,
            `/api/v2/service-admin/client/${payload.container.clientId}/service-config/absence-management/standard-question/question/${payload.container.id}/assignment`,
            a,
          );
        }),
      ]);
    }

    yield call(onRefresh, {
      clientId: payload.container.clientId,
      stableId: payload.container.id,
    });
    yield all([
      put(actionCreators.ShowEditStandardQuestionAssignmentsModal(false)),
      put(
        push(
          route("ADMINISTRATION_CLIENT_FEATURES_PATH", {
            clientId: payload.container.clientId,
          }),
        ),
      ),
      put(sendSuccessMessage("STANDARD_QUESTION_UPDATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateStandardQuestionAssignmentsError(e.message));
  }
}

export function* StandardQuestionsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetStandardQuestions, onGetStandardQuestions),
    takeEvery(AddNewStandardQuestion, onAddNewStandardQuestion),
    takeEvery(GetStandardQuestion, onGetStandardQuestion),
    takeEvery(UpdateStandardQuestion, onUpdateStandardQuestion),
    takeEvery(UpdateStandardQuestionAssignments, onUpdateStandardQuestionAssignments),
  ]);
}

export default StandardQuestionsSagas;
